import React from "react";
import {
  Container,
  Grid,
  Skeleton,
  Box,
  Paper,
  useTheme,
} from "@mui/material";

/**
 * Shimmer loading component for CreateStoryPage
 * Matches the structure of the Create Story form with glass-card styling
 */
const CreateStoryPageShimmer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 6 } }}>
      {/* Page Header Shimmer */}
      <Box
        sx={{
          textAlign: "center",
          mb: 6,
        }}
      >
        <Skeleton
          variant="text"
          width={300}
          height={56}
          sx={{
            mx: "auto",
            mb: 2,
            borderRadius: 1,
            bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
          }}
        />
        <Skeleton
          variant="text"
          width={600}
          height={32}
          sx={{
            mx: "auto",
            borderRadius: 1,
            bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
          }}
        />
      </Box>

      {/* Form Grid Shimmer */}
      <Grid container spacing={4}>
        {/* Cover Section Shimmer - Left Side */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              position: "sticky",
              top: 100,
              height: "fit-content",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: "transparent",
                backdropFilter: "blur(12px)",
                border: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)"
                  }`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.1)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              {/* Cover Image Placeholder */}
              <Skeleton
                variant="rectangular"
                sx={{
                  width: "100%",
                  aspectRatio: "3/4",
                  borderRadius: 2,
                  mb: 2,
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)",
                }}
              />

              {/* Upload Button Shimmer */}
              <Skeleton
                variant="rounded"
                width="100%"
                height={40}
                sx={{
                  mb: 2,
                  borderRadius: 999,
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                }}
              />

              {/* Checkbox Label Shimmer */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width={20}
                  height={20}
                  sx={{
                    borderRadius: 0.5,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)",
                  }}
                />
                <Skeleton
                  variant="text"
                  width={150}
                  height={20}
                  sx={{
                    borderRadius: 1,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)",
                  }}
                />
              </Box>
            </Paper>
          </Box>
        </Grid>

        {/* Details Section Shimmer - Right Side */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              backgroundColor: "transparent",
              backdropFilter: "blur(12px)",
              border: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)"
                }`,
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 2px 8px rgba(0,0,0,0.1)"
                  : "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <Grid container spacing={3}>
              {/* Title Field Shimmer */}
              <Grid item xs={12}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={56}
                  sx={{
                    borderRadius: 999,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  }}
                />
              </Grid>

              {/* Subtitle Field Shimmer */}
              <Grid item xs={12}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={56}
                  sx={{
                    borderRadius: 999,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  }}
                />
              </Grid>

              {/* Description Field Shimmer */}
              <Grid item xs={12}>
                <Skeleton
                  variant="text"
                  width={120}
                  height={24}
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)",
                  }}
                />
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={250}
                  sx={{
                    borderRadius: 3,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(0,0,0,0.04)",
                  }}
                />
              </Grid>

              {/* Category Field Shimmer */}
              <Grid item xs={12} sm={6}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={56}
                  sx={{
                    borderRadius: 999,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  }}
                />
              </Grid>

              {/* Language Field Shimmer */}
              <Grid item xs={12} sm={6}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={56}
                  sx={{
                    borderRadius: 999,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  }}
                />
              </Grid>

              {/* Copyright Field Shimmer */}
              <Grid item xs={12} sm={6}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={56}
                  sx={{
                    borderRadius: 999,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  }}
                />
              </Grid>

              {/* Target Audience Field Shimmer */}
              <Grid item xs={12} sm={6}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={56}
                  sx={{
                    borderRadius: 999,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  }}
                />
              </Grid>

              {/* Main Characters Section Shimmer */}
              <Grid item xs={12}>
                <Skeleton
                  variant="text"
                  width={180}
                  height={24}
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)",
                  }}
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={56}
                    sx={{
                      borderRadius: 999,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.04)",
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={56}
                    sx={{
                      borderRadius: 999,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.04)",
                    }}
                  />
                </Box>
              </Grid>

              {/* Tags Section Shimmer */}
              <Grid item xs={12}>
                <Skeleton
                  variant="text"
                  width={100}
                  height={24}
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)",
                  }}
                />
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      width={80}
                      height={32}
                      sx={{
                        borderRadius: 999,
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)",
                      }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Mature Content Checkbox Shimmer */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Skeleton
                    variant="rectangular"
                    width={20}
                    height={20}
                    sx={{
                      borderRadius: 0.5,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)",
                    }}
                  />
                  <Skeleton
                    variant="text"
                    width={200}
                    height={20}
                    sx={{
                      borderRadius: 1,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.06)",
                    }}
                  />
                </Box>
              </Grid>

              {/* Submit Button Shimmer */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Skeleton
                    variant="rounded"
                    width={150}
                    height={48}
                    sx={{
                      borderRadius: 20,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CreateStoryPageShimmer;

